import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/atoms/card";
import { JsonHighlight } from "@/components/ui/atoms/json-highlight";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Status type
// ---------------------------------------------------------------------------

export type ToolAuthorizationStatus =
  | "loading"
  | "pending"
  | "completed"
  | "failed";

// ---------------------------------------------------------------------------
// CVA variants
// ---------------------------------------------------------------------------

const toolAuthorizationVariants = cva("my-2 gap-0 py-0", {
  variants: {
    status: {
      loading: "",
      pending: "",
      completed: "border-green-100 dark:border-green-950",
      failed: "border-red-100 dark:border-red-950",
    },
  },
  defaultVariants: { status: "loading" },
});

const toolAuthorizationIconWrapperVariants = cva("shrink-0 rounded-full p-2", {
  variants: {
    status: {
      loading: "bg-muted",
      pending: "bg-orange-50 dark:bg-orange-950/50",
      completed: "bg-green-50 dark:bg-green-950/50",
      failed: "bg-red-50 dark:bg-red-950/50",
    },
  },
  defaultVariants: { status: "loading" },
});

const toolAuthorizationIconColorVariants = cva("size-5", {
  variants: {
    status: {
      loading: "text-muted-foreground",
      pending: "text-orange-500 dark:text-orange-400",
      completed: "text-green-500 dark:text-green-400",
      failed: "text-red-500 dark:text-red-400",
    },
  },
  defaultVariants: { status: "loading" },
});

const toolAuthorizationMessageVariants = cva(
  "flex items-center gap-2 rounded-md p-3 text-xs",
  {
    variants: {
      status: {
        loading: "bg-muted text-muted-foreground",
        pending:
          "bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300",
        completed:
          "bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-300",
        failed: "bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300",
      },
    },
    defaultVariants: { status: "loading" },
  }
);

// ---------------------------------------------------------------------------
// Component prop types
// ---------------------------------------------------------------------------

export type ToolAuthorizationProps = React.ComponentProps<typeof Card> &
  VariantProps<typeof toolAuthorizationVariants>;

export type ToolAuthorizationHeaderProps = Omit<
  React.ComponentProps<"div">,
  "title"
> & {
  status?: ToolAuthorizationStatus;
  icon?: React.ComponentType<{ className?: string }>;
  /** Optional toolkit brand icon rendered next to the title text. */
  toolkitIcon?: React.ComponentType<{ className?: string }>;
  title?: React.ReactNode;
  description?: React.ReactNode;
};

export type ToolAuthorizationMessageProps = React.ComponentProps<"div"> &
  VariantProps<typeof toolAuthorizationMessageVariants>;

export type ToolAuthorizationArgsProps = {
  args: Record<string, unknown>;
} & React.ComponentProps<"div">;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Root card wrapper. Accepts a `status` prop that controls the border colour.
 */
function ToolAuthorization({
  className,
  status,
  ...props
}: ToolAuthorizationProps) {
  return (
    <Card
      className={cn(toolAuthorizationVariants({ status }), className)}
      data-slot="tool-authorization"
      {...props}
    />
  );
}

/**
 * Header row with built-in icon wrapper, title, and description.
 *
 * Pass `icon` (a component reference like `CheckCircle2`), `title`,
 * `description`, and the same `status` used on `ToolAuthorization` to get the
 * standard layout with correct status colours applied automatically.
 */
function ToolAuthorizationHeader({
  className,
  status,
  icon: Icon,
  toolkitIcon: ToolkitIcon,
  title,
  description,
  children,
  ...props
}: ToolAuthorizationHeaderProps) {
  return (
    <CardHeader className="px-3 pt-3 pb-0">
      <div
        className={cn("flex items-start gap-3 sm:items-center", className)}
        data-slot="tool-authorization-header"
        {...props}
      >
        {Icon && (
          <div className={cn(toolAuthorizationIconWrapperVariants({ status }))}>
            <Icon
              className={cn(toolAuthorizationIconColorVariants({ status }))}
            />
          </div>
        )}
        {(title || description) && (
          <div>
            {title && (
              <h3 className="flex items-center gap-1.5 font-medium text-card-foreground text-sm">
                {ToolkitIcon && <ToolkitIcon className="size-4 shrink-0" />}
                {title}
              </h3>
            )}
            {description && (
              <p className="mt-0.5 text-muted-foreground text-xs">
                {description}
              </p>
            )}
          </div>
        )}
        {children}
      </div>
    </CardHeader>
  );
}

/**
 * Main body area below the header. Renders a vertical flex column.
 */
function ToolAuthorizationContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <CardContent className="p-3 pt-2">
      <div
        className={cn("flex flex-col gap-2", className)}
        data-slot="tool-authorization-content"
        {...props}
      />
    </CardContent>
  );
}

/**
 * Coloured message/callout block inside the content area.
 * Use the same `status` as the root `ToolAuthorization`.
 */
function ToolAuthorizationMessage({
  className,
  status,
  ...props
}: ToolAuthorizationMessageProps) {
  return (
    <div
      className={cn(
        toolAuthorizationMessageVariants({ status }),
        "items-start sm:items-center",
        className
      )}
      data-slot="tool-authorization-message"
      {...props}
    />
  );
}

/**
 * Renders tool arguments as a formatted JSON pre block.
 * Renders nothing when `args` is empty.
 */
function ToolAuthorizationArgs({
  args,
  className,
  ...props
}: ToolAuthorizationArgsProps) {
  if (Object.keys(args).length === 0) {
    return null;
  }
  return (
    <div
      className={cn("w-full rounded-lg bg-muted p-2 sm:p-2.5", className)}
      data-slot="tool-authorization-args"
      {...props}
    >
      <h4 className="mb-1 font-medium text-xs">Tool Arguments:</h4>
      <JsonHighlight
        className="text-[11px] sm:text-xs"
        code={JSON.stringify(args, null, 2)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  ToolAuthorization,
  ToolAuthorizationArgs,
  ToolAuthorizationContent,
  ToolAuthorizationHeader,
  ToolAuthorizationMessage,
};
